import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  HomeOutlined,
  TimelineOutlined,
  SchoolOutlined,
  PersonOutlined,
  AppsOutlined,
} from '@mui/icons-material';

/**
 * iOS system font stack for authenticity.
 */
export const IOS_FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", sans-serif';
export const IOS_BLUE = '#007AFF';
export const IOS_GRAY_BG = '#F2F2F7';
export const IOS_GRAY_FIELD = '#E5E5EA';
export const IOS_GRAY_TEXT = '#8E8E93';
export const IOS_SEPARATOR = '#C6C6C8';

/**
 * iOS status bar (top, 44px tall). Shows time, signal, wifi, battery.
 */
export const StatusBar = ({ time = '9:41', dark = false }) => {
  const fg = dark ? '#fff' : '#000';
  return (
    <Box sx={{
      height: 44,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 3,
      pt: 1.2,
      fontFamily: IOS_FONT,
      fontSize: '15px',
      fontWeight: 600,
      color: fg,
      position: 'relative',
      zIndex: 2,
    }}>
      <Box>{time}</Box>
      {/* Dynamic Island */}
      <Box sx={{
        position: 'absolute',
        top: 10,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 120,
        height: 34,
        bgcolor: '#000',
        borderRadius: 20,
      }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        {/* Signal bars */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px' }}>
          {[4, 6, 8, 10].map((h, i) => (
            <Box key={i} sx={{ width: 3, height: h, bgcolor: fg, borderRadius: '1px' }} />
          ))}
        </Box>
        {/* Wifi glyph (triangle) */}
        <Box sx={{
          width: 0, height: 0,
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderBottom: `10px solid ${fg}`,
          opacity: 0.9,
        }} />
        {/* Battery */}
        <Box sx={{
          width: 26,
          height: 12,
          border: `1px solid ${fg}`,
          borderRadius: '3px',
          p: '1px',
          position: 'relative',
          ml: 0.5,
        }}>
          <Box sx={{ width: '80%', height: '100%', bgcolor: fg, borderRadius: '1px' }} />
          <Box sx={{ position: 'absolute', right: -3, top: 3, width: 2, height: 4, bgcolor: fg, borderRadius: '0 1px 1px 0' }} />
        </Box>
      </Box>
    </Box>
  );
};

/**
 * iOS navigation bar (large title or inline).
 * Translucent frosted background.
 */
export const NavBar = ({
  title,
  largeTitle = false,
  leadingButton = null,  // { label, onClick } or { chevron: true, label }
  trailingButton = null, // { label, onClick }
  subtitle = null,
}) => {
  return (
    <Box sx={{
      bgcolor: 'rgba(249, 249, 249, 0.94)',
      backdropFilter: 'blur(20px)',
      borderBottom: largeTitle ? 'none' : `0.5px solid ${IOS_SEPARATOR}`,
      fontFamily: IOS_FONT,
    }}>
      <Box sx={{
        height: 44,
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        position: 'relative',
      }}>
        {/* Leading */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          {leadingButton && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              color: IOS_BLUE,
              fontSize: '17px',
              fontWeight: 400,
              cursor: 'pointer',
            }}>
              {leadingButton.chevron && (
                <Box sx={{ fontSize: '22px', mr: 0.3, fontWeight: 300, lineHeight: 1 }}>
                  &#10094;
                </Box>
              )}
              {leadingButton.label}
            </Box>
          )}
        </Box>
        {/* Inline centered title (only when not large) */}
        {!largeTitle && (
          <Box sx={{
            position: 'absolute',
            left: 0, right: 0,
            textAlign: 'center',
            fontSize: '17px',
            fontWeight: 600,
            color: '#000',
            pointerEvents: 'none',
          }}>
            {title}
            {subtitle && (
              <Typography sx={{
                fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT,
                fontWeight: 400, mt: '-2px',
              }}>{subtitle}</Typography>
            )}
          </Box>
        )}
        {/* Trailing */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          {trailingButton && (
            <Box sx={{
              color: IOS_BLUE,
              fontSize: '17px',
              fontWeight: trailingButton.bold ? 600 : 400,
              cursor: 'pointer',
            }}>{trailingButton.label}</Box>
          )}
        </Box>
      </Box>
      {largeTitle && (
        <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
          <Typography sx={{
            fontFamily: IOS_FONT,
            fontSize: '34px',
            fontWeight: 700,
            color: '#000',
            letterSpacing: '0.37px',
            lineHeight: 1.1,
          }}>{title}</Typography>
          {subtitle && (
            <Typography sx={{
              fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT, mt: 0.3,
            }}>{subtitle}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * iOS bottom tab bar. 5 slots.
 */
export const TabBar = ({ activeIndex = 0, tabs }) => {
  const defaultTabs = [
    { label: 'Home', icon: <HomeOutlined /> },
    { label: 'Progress', icon: <TimelineOutlined /> },
    { label: 'Apps', icon: <AppsOutlined /> },
    { label: 'Classes', icon: <SchoolOutlined /> },
    { label: 'Profile', icon: <PersonOutlined /> },
  ];
  const items = tabs || defaultTabs;
  return (
    <Box sx={{
      height: 83,
      bgcolor: 'rgba(249, 249, 249, 0.94)',
      backdropFilter: 'blur(20px)',
      borderTop: `0.5px solid ${IOS_SEPARATOR}`,
      display: 'flex',
      fontFamily: IOS_FONT,
      pb: '34px', // home bar area
    }}>
      {items.map((t, i) => {
        const active = i === activeIndex;
        return (
          <Box key={i} sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 0.8,
            color: active ? IOS_BLUE : IOS_GRAY_TEXT,
            cursor: 'pointer',
          }}>
            <Box sx={{ fontSize: 26, lineHeight: 1, '& svg': { fontSize: 26 } }}>
              {t.icon}
            </Box>
            <Typography sx={{
              fontFamily: IOS_FONT, fontSize: '10px',
              fontWeight: 500, mt: 0.3, letterSpacing: '0.1px',
            }}>{t.label}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Home bar (bottom indicator).
 */
export const HomeBar = ({ dark = false }) => (
  <Box sx={{
    position: 'absolute',
    left: '50%',
    bottom: 8,
    transform: 'translateX(-50%)',
    width: 134,
    height: 5,
    bgcolor: dark ? '#fff' : '#000',
    borderRadius: 3,
    opacity: 0.9,
    zIndex: 5,
  }} />
);

/**
 * iPhone frame wrapper. Child content renders inside a 375x812 viewport
 * with notch/Dynamic Island, rounded corners, and home bar overlay.
 *
 * The child is responsible for laying out its own status bar / nav / tab bar.
 */
const IPhoneFrame = ({ children, title, caption }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1.5,
  }}>
    {title && (
      <Typography sx={{
        fontFamily: IOS_FONT, fontSize: '0.95rem', fontWeight: 600, color: '#263238',
      }}>{title}</Typography>
    )}
    <Box sx={{
      position: 'relative',
      width: 395,
      height: 832,
      bgcolor: '#000',
      borderRadius: '55px',
      p: '10px',
      boxShadow: '0 18px 40px rgba(0,0,0,0.24), inset 0 0 0 2px #222',
    }}>
      <Box sx={{
        width: 375,
        height: 812,
        borderRadius: '47px',
        bgcolor: IOS_GRAY_BG,
        overflow: 'hidden',
        position: 'relative',
        fontFamily: IOS_FONT,
      }}>
        {children}
      </Box>
    </Box>
    {caption && (
      <Typography sx={{
        fontFamily: IOS_FONT, fontSize: '0.75rem', color: '#78909c', maxWidth: 395, textAlign: 'center',
      }}>{caption}</Typography>
    )}
  </Box>
);

export default IPhoneFrame;
